import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

/**
 * Snapshot returned by `useProvidersMetroCounts`. Same shape as
 * `UseProvidersOgCountsResult` (sibling endpoint, sibling hook).
 */
export interface UseProvidersMetroCountsResult {
  /** Map of metro id (slug) -> provider count, or null while loading
   *  the first response (or on error). */
  readonly counts: Readonly<Record<string, number>> | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Per-metro provider count fetcher backing the Providers Metro Area
 * combobox badges (2026-06-05, Maria).
 *
 * Sibling of `useProvidersOgCounts` — same race-guard + cache pattern,
 * just keyed on metro id instead of OG name. Refires when either
 * `ogName` or `search` changes so the badges stay aligned with the
 * table results.
 *
 * Behavior:
 *   - Fires GET /api/providers/metro-counts when either input changes.
 *     ogName may be empty (universe-wide counts); search may be empty
 *     (no name narrowing). Both empty is valid -- counts across all
 *     providers.
 *   - Caches by composite key `ogName|search` per hook instance.
 *   - Stale-resolution guard via a generation counter -- in-flight
 *     responses whose inputs have since changed are dropped.
 *   - Errors don't surface to the user -- the dropdown simply renders
 *     without badges. The badges are a discoverability hint on top of
 *     an already-functional filter.
 */
export function useProvidersMetroCounts(
  ogName: string,
  search: string = '',
): UseProvidersMetroCountsResult {
  const [counts, setCounts] = useState<Readonly<Record<string, number>> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const cacheRef = useRef<Map<string, Readonly<Record<string, number>>>>(new Map());
  const generationRef = useRef<number>(0);

  useEffect(() => {
    generationRef.current += 1;
    const gen = generationRef.current;
    const cacheKey = `${ogName}|${search}`;

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setCounts(cached);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    api.providers
      .metroCounts({
        groupId: ogName || undefined,
        search: search || undefined,
      })
      .then((res) => {
        if (gen !== generationRef.current) return;
        cacheRef.current.set(cacheKey, res.counts);
        setCounts(res.counts);
      })
      .catch((cause: unknown) => {
        if (gen !== generationRef.current) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setCounts(null);
      })
      .finally(() => {
        if (gen !== generationRef.current) return;
        setIsLoading(false);
      });
  }, [ogName, search]);

  return { counts, isLoading, error };
}
